import React, { useEffect, useState } from "react";
import axios from "axios";
import UseContext from "hooks/useContext";
import useFetchData from "hooks/useFetchData";
import SectionWrapper, {
    FormField,
} from "pages/createMission/components/SectionWrapper";
import StyledInput from "components/styledInput/StyledInput";
import AddressGeoApi from "components/AddressGeoApi/AddressGeoApi";
import Tooltip from "components/tooltips/Tooltips";
import { InformationIcon } from "assets/icons";
import {
    formatHourlyRate,
    isBelowMinimumWage,
    useMinimumWage,
} from "pages/missionTemplates/utils/templateMinimumWage";

const LocationSalaryTemplate = () => {
    const { templateData, setTemplateData, companyData, language } =
        UseContext();
    const { userData } = useFetchData();
    const minimumWage = useMinimumWage();
    const [address, setAddress] = useState(templateData.location || "");
    const [addressFieldMode, setAddressFieldMode] = useState({
        canUseCompanyAddress: false,
        usingCompanyAddress: false,
    });
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.locationSalary.${key}`, fallback);

    const addressFormLabel =
        addressFieldMode.canUseCompanyAddress &&
        addressFieldMode.usingCompanyAddress
            ? t("addressLabel", "Adresse")
            : t("addressSearchLabel", "Rechercher un lieu");
    const formattedMinimumWage =
        minimumWage == null ? null : formatHourlyRate(minimumWage);
    const showBelowMinimumWage = isBelowMinimumWage(templateData, minimumWage);

    // Only empty fields are prefilled: an edited template keeps its manager.
    useEffect(() => {
        if (!userData?.firstname || !userData?.lastname) return;
        setTemplateData((prev) => ({
            ...prev,
            contact_name:
                prev.contact_name === ""
                    ? `${userData.firstname} ${userData.lastname}`
                    : prev.contact_name,
            contact_poste:
                prev.contact_poste === ""
                    ? t("contactPosteDefault", "Responsable")
                    : prev.contact_poste,
        }));
    }, [userData]); // eslint-disable-line

    useEffect(() => {
        if (
            address &&
            typeof address === "object" &&
            Object.keys(address).length > 0
        ) {
            setTemplateData((prev) => ({ ...prev, location: address }));
        }
    }, [address, setTemplateData]);

    useEffect(() => {
        if (
            (!templateData.location ||
                (typeof templateData.location === "object" &&
                    Object.keys(templateData.location).length === 0)) &&
            companyData.location
        ) {
            const abortController = new AbortController();
            axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}address/${companyData.location}`,
                withCredentials: true,
                signal: abortController.signal,
            })
                .then((res) => {
                    if (res.data) setAddress(res.data);
                })
                .catch((err) => {
                    if (
                        err.code !== "ECONNABORTED" &&
                        err.code !== "ERR_CANCELED" &&
                        err.name !== "CanceledError"
                    )
                        console.log(err);
                });
            return () => abortController.abort();
        }
    }, [templateData.location, companyData.location]);

    const handleSalaryChange = (value) => {
        setTemplateData((prev) => ({ ...prev, salary: value }));
    };

    const toggleIndexed = () => {
        setTemplateData((prev) => {
            const next = !prev.isIndexedToMinimumWage;
            return {
                ...prev,
                isIndexedToMinimumWage: next,
                salary:
                    next && minimumWage != null
                        ? minimumWage.toFixed(2)
                        : prev.salary,
            };
        });
    };

    const updateContact = (field) => (e) =>
        setTemplateData((prev) => ({ ...prev, [field]: e.target.value }));

    return (
        <div className="flex flex-col gap-6">
            <SectionWrapper
                title={t("locationTitle", "Lieu de travail")}
                description={t(
                    "locationDescription",
                    "Où l'intérimaire doit-il se présenter ?"
                )}
            >
                <FormField label={addressFormLabel}>
                    <AddressGeoApi
                        addressGeoValue={setAddress}
                        addressValue={address}
                        isAddressCompany={true}
                        page={"missionTemplate"}
                        embeddedInFormField
                        onAddressModeChange={setAddressFieldMode}
                    />
                </FormField>
            </SectionWrapper>
            <SectionWrapper
                title={t("salaryTitle", "Rémunération")}
                description={t(
                    "salaryDescription",
                    "Taux horaire brut, hors primes et majorations."
                )}
            >
                <div className="flex flex-col">
                    <StyledInput
                        type={"text"}
                        value={templateData.salary}
                        onChange={(e) => handleSalaryChange(e.target.value)}
                        placeholder={t("salaryPlaceholder", "Ex : 12,50")}
                        isDisabled={templateData.isIndexedToMinimumWage}
                        error={
                            showBelowMinimumWage
                                ? t(
                                      "belowMinimumWage",
                                      "Le taux doit être au moins égal au SMIC ({smic} €/h)."
                                  ).replace("{smic}", formattedMinimumWage)
                                : undefined
                        }
                        unit={
                            <div className="flex items-center gap-2">
                                <Tooltip
                                    text={t(
                                        "indexedTooltip",
                                        "Quand la fiche est indexée au SMIC, le taux suit automatiquement la dernière valeur officielle."
                                    )}
                                    placement="bottom"
                                >
                                    <div className="flex items-center cursor-pointer">
                                        <InformationIcon color={"#2CAFFF"} wh={15} />
                                    </div>
                                </Tooltip>
                                <span className="ft-sm text-gray-500">
                                    {t("salaryUnit", "€/h")}
                                </span>
                            </div>
                        }
                    />
                    <div className="flex justify-end my-2">
                        <div className="w-fit">
                            <StyledInput
                                type={"checkbox"}
                                checked={!!templateData.isIndexedToMinimumWage}
                                onChange={toggleIndexed}
                                label={t(
                                    "indexedLabel",
                                    "Indexer au SMIC ({smic})"
                                ).replace(
                                    "{smic}",
                                    formattedMinimumWage
                                        ? `${formattedMinimumWage} €/h`
                                        : "—"
                                )}
                            />
                        </div>
                    </div>
                </div>
            </SectionWrapper>
            <SectionWrapper
                title={t("contactTitle", "Responsable sur place")}
                description={t(
                    "contactDescription",
                    "La personne que l'intérimaire contactera en arrivant."
                )}
            >
                <StyledInput
                    type={"text"}
                    label={t("contactName", "Nom et prénom")}
                    value={templateData.contact_name}
                    onChange={updateContact("contact_name")}
                />
                <StyledInput
                    type={"text"}
                    label={t("contactPoste", "Fonction")}
                    value={templateData.contact_poste}
                    onChange={updateContact("contact_poste")}
                />
            </SectionWrapper>
        </div>
    );
};

export default LocationSalaryTemplate;
